import { useMutation } from '@tanstack/react-query';
import { administrationClient, unwrap, createResource } from '@/shared/api-client';
import { apiUrl } from '@/shared/api-client/url';

export interface BrandProfile {
  displayName: string;
  primaryColor: string;
  hasLogo: boolean;
}

export interface UpdateBrandInput {
  displayName?: string;
  primaryColor?: string;
}

/** The cap and accepted MIME types mirror the backend's BrandService validation. */
export const MAX_LOGO_BYTES = 500 * 1024;
export const ALLOWED_LOGO_TYPES = ['image/png', 'image/svg+xml', 'image/jpeg'] as const;

export const getBrand = (): Promise<BrandProfile> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/brand'),
  ) as Promise<BrandProfile>;

export const updateBrand = (input: UpdateBrandInput): Promise<BrandProfile> =>
  unwrap(
    administrationClient.PUT('/api/v1/franchise-administration/brand', { body: input }),
  ) as Promise<BrandProfile>;

export const uploadLogo = (file: File): Promise<BrandProfile> =>
  unwrap(
    administrationClient.PUT('/api/v1/franchise-administration/brand/logo', {
      // Build the multipart body ourselves so the browser sets the boundary;
      // the schema types `file` as a binary string, hence the cast.
      body: { file } as unknown as { file: string },
      bodySerializer: () => {
        const form = new FormData();
        form.append('file', file);
        return form;
      },
    }),
  ) as Promise<BrandProfile>;

/** Stable URL for the served logo; cache-busted by the profile's last update via the caller. */
export const logoUrl = (): string => apiUrl('api/v1/franchise-administration/brand/logo');

/** The brand singleton through the shared resource factory (ADR 0012): `list`
 *  fetches the one profile, `update` saves it. `uploadLogo` composes on the
 *  resource's `useInvalidate` since it shares the brand cache key. */
const brand = createResource({
  key: ['brand'],
  list: getBrand,
  update: updateBrand,
});

export const useBrand = brand.useList;
export const useUpdateBrand = brand.useUpdate;

export function useUploadLogo() {
  const invalidate = brand.useInvalidate();
  return useMutation({ mutationFn: uploadLogo, onSuccess: () => invalidate() });
}
